import React, { useState, useEffect } from "react";
import axios from "axios";
import { RotateCcw, User, Clock, Truck, Calendar, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DESTINATIONS = [
  { id: "to_inspector", label: "Return to Inspector", icon: User, desc: "Reassign to an inspector's active list" },
  { id: "to_hold", label: "Send to Hold", icon: Clock, desc: "Place on hold with a release date" },
  { id: "to_dispatch", label: "Return to Dispatch", icon: Truck, desc: "Send back to the dispatch pool" },
  { id: "to_scheduling", label: "Return to Scheduling", icon: Calendar, desc: "Send back to scheduling queue" },
];

const ReopenModal = ({ orderIds, orderNumbers, onClose, onSuccess, defaultInspector = "" }) => {
  const [destination, setDestination] = useState("");
  const [reason, setReason] = useState("");
  const [inspectorName, setInspectorName] = useState(defaultInspector);
  const [holdUntilDate, setHoldUntilDate] = useState("");
  const [holdReason, setHoldReason] = useState("");
  const [inspectors, setInspectors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isBatch = orderIds.length > 1;

  useEffect(() => {
    axios.get(`${API}/inspectors`).then(res => {
      setInspectors(res.data.filter(i => i.active === "yes" || i.active === undefined || i.active === null));
    }).catch(() => {});
  }, []);

  const canSubmit = () => {
    if (!destination || !reason.trim()) return false;
    if (destination === "to_inspector" && !inspectorName) return false;
    if (destination === "to_hold" && !holdUntilDate) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    setSaving(true);
    setError("");

    const payload = {
      destination,
      reopen_reason: reason.trim(),
      inspector_name: destination === "to_inspector" ? inspectorName : null,
      hold_until_date: destination === "to_hold" ? holdUntilDate : null,
      hold_reason: destination === "to_hold" ? (holdReason || "Reopened to Hold") : null,
    };

    try {
      if (isBatch) {
        const res = await axios.post(`${API}/orders/batch-reopen`, { order_ids: orderIds, ...payload });
        onSuccess(`${res.data.reopened} order(s) reopened successfully`);
      } else {
        await axios.post(`${API}/orders/${orderIds[0]}/reopen`, payload);
        onSuccess("Order reopened successfully");
      }
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to reopen order");
    } finally {
      setSaving(false);
    }
  };

  const displayLabel = isBatch ? `${orderIds.length} orders` : (orderNumbers?.[0] || "Order");

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()} data-testid="reopen-modal">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#2F5D8A]/10 rounded-lg">
              <RotateCcw className="h-5 w-5 text-[#2F5D8A]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#1F2937]">Reopen Order</h3>
              <p className="text-gray-500 text-xs mt-0.5">{displayLabel}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors" data-testid="reopen-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-2.5 bg-[#C07070]/10 border border-[#C07070]/25 rounded-lg text-[#8B4545] text-sm">{error}</div>
        )}

        {/* Destination Selector */}
        <div className="mb-4">
          <label className="block text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-2">Reopen Destination *</label>
          <div className="grid grid-cols-2 gap-2">
            {DESTINATIONS.map(d => {
              const Icon = d.icon;
              const active = destination === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => setDestination(d.id)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    active
                      ? "bg-[#2F5D8A]/8 border-[#2F5D8A]/40 ring-1 ring-[#2F5D8A]/30"
                      : "bg-gray-50 border-gray-200 hover:border-gray-300 hover:bg-gray-100"
                  }`}
                  data-testid={`reopen-dest-${d.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`h-3.5 w-3.5 ${active ? "text-[#2F5D8A]" : "text-gray-400"}`} />
                    <span className={`text-xs font-semibold ${active ? "text-[#2F5D8A]" : "text-gray-700"}`}>{d.label}</span>
                  </div>
                  <p className="text-[10px] text-gray-400 leading-tight">{d.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Inspector Selector (if destination = to_inspector) */}
        {destination === "to_inspector" && (
          <div className="mb-4">
            <label className="block text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5">Assign to Inspector *</label>
            <select
              value={inspectorName}
              onChange={e => setInspectorName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#2F5D8A] focus:outline-none bg-white"
              data-testid="reopen-inspector-select"
            >
              <option value="">Select inspector...</option>
              {inspectors.map(i => (
                <option key={i.id || i.name} value={i.name}>{i.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Hold fields (if destination = to_hold) */}
        {destination === "to_hold" && (
          <div className="mb-4 space-y-3 p-3 bg-[#EDE5D6]/30 border border-[#D1A054]/20 rounded-lg">
            <div>
              <label className="block text-[10px] font-semibold text-[#7A6535] uppercase tracking-wider mb-1.5">Hold Until Date *</label>
              <input
                type="date"
                value={holdUntilDate}
                onChange={e => setHoldUntilDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#D1A054] focus:outline-none"
                data-testid="reopen-hold-date"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-[#7A6535] uppercase tracking-wider mb-1.5">Hold Reason</label>
              <select
                value={holdReason}
                onChange={e => setHoldReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] focus:border-[#D1A054] focus:outline-none bg-white"
                data-testid="reopen-hold-reason"
              >
                <option value="">Select reason...</option>
                <option value="Transmission window not open">Transmission window not open</option>
                <option value="Waiting for transmission date">Waiting for transmission date</option>
                <option value="Window stop">Window stop</option>
                <option value="Client/date restriction">Client/date restriction</option>
                <option value="Reopened to Hold">Reopened to Hold</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        )}

        {/* Reason (always required) */}
        <div className="mb-5">
          <label className="block text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider mb-1.5">Reason for Reopening *</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Explain why this order is being reopened..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#1F2937] placeholder-gray-400 focus:border-[#2F5D8A] focus:outline-none resize-none"
            data-testid="reopen-reason"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg transition-colors"
            data-testid="reopen-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !canSubmit()}
            className="flex-1 px-4 py-2.5 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
            data-testid="reopen-confirm-btn"
          >
            <RotateCcw className="h-4 w-4" />
            {saving ? "Reopening..." : "Confirm Reopen"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReopenModal;
